import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, FormControl, FormLabel,
    Input, Modal, ModalDialog, Option, Select, Sheet, Stack, Table, Textarea, Typography,
} from '@mui/joy';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import { useAdmin } from '../../AdminContext';
import {
    APIAdminVerifications, APIAdminVerificationDetail,
    APIAdminVerificationApprove, APIAdminVerificationReject, APIAdminVerificationRequestChanges,
    APIAdminProfileRiskFlags, APIAdminProfileRiskFlagAdd, APIAdminProfileRiskFlagRemove,
    verifError,
} from '../../api_admin_verifications';
import { APIAdminFaceCheck, APIAdminProfilePhotoDuplicates } from '../../api_admin_antifraud';
import AiStatusBadge from '../../components/ui/AiStatusBadge';
import AiModerationModeToggle from '../../components/ui/AiModerationModeToggle';

const STATUS = {
    pending: { label: 'На проверке', color: 'warning' },
    approved: { label: 'Подтверждена', color: 'success' },
    rejected: { label: 'Отклонена', color: 'danger' },
    changes_requested: { label: 'Нужны правки', color: 'warning' },
};
const STATUS_OPTIONS = [
    { value: '', label: 'Все статусы' },
    { value: 'pending', label: 'На проверке' },
    { value: 'approved', label: 'Подтверждённые' },
    { value: 'rejected', label: 'Отклонённые' },
    { value: 'changes_requested', label: 'Нужны правки' },
];

function Photo({ label, url }) {
    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>{label}</Typography>
            {url ? (
                <a href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={label} width={120} height={120}
                         style={{ objectFit: 'cover', borderRadius: 8 }} />
                </a>
            ) : (
                <Box sx={{ width: 120, height: 120, borderRadius: 8, bgcolor: 'neutral.softBg',
                           display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography level="body-xs" color="neutral">нет</Typography>
                </Box>
            )}
        </Box>
    );
}

function DetailModal({ token, item, onClose, onChanged }) {
    const [v, setV] = useState(null);
    const [flags, setFlags] = useState([]);
    const [available, setAvailable] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [reason, setReason] = useState('');
    const [note, setNote] = useState('');
    const [newFlag, setNewFlag] = useState('');
    const [flagNote, setFlagNote] = useState('');
    const [faceChecking, setFaceChecking] = useState(false);
    const [faceMsg, setFaceMsg] = useState(null);
    const [dupes, setDupes] = useState(null);
    const [dupesLoading, setDupesLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const [d, f] = await Promise.all([
                APIAdminVerificationDetail(token, item.id),
                APIAdminProfileRiskFlags(token, item.profile_id),
            ]);
            setV(d?.verification || null);
            setFlags(f?.items || []);
            setAvailable(f?.available || []);
        } catch (e) { setError(verifError(e)); } finally { setLoading(false); }
    }, [token, item]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn) => {
        setBusy(true); setError(null);
        try { await fn(); onChanged(); onClose(); }
        catch (e) { setError(verifError(e)); setBusy(false); }
    };
    const approve = () => act(() => APIAdminVerificationApprove(token, item.id, note));
    const reject = () => {
        if (!reason.trim()) { setError('Укажите причину отклонения'); return; }
        act(() => APIAdminVerificationReject(token, item.id, reason.trim()));
    };
    const requestChanges = () => {
        if (!note.trim()) { setError('Опишите, что исправить'); return; }
        act(() => APIAdminVerificationRequestChanges(token, item.id, note.trim()));
    };

    const addFlag = async () => {
        if (!newFlag) return;
        try {
            await APIAdminProfileRiskFlagAdd(token, item.profile_id, newFlag, flagNote.trim());
            setNewFlag(''); setFlagNote('');
            const f = await APIAdminProfileRiskFlags(token, item.profile_id);
            setFlags(f?.items || []);
        } catch (e) { setError(verifError(e)); }
    };
    const removeFlag = async (flagType) => {
        try {
            await APIAdminProfileRiskFlagRemove(token, item.profile_id, flagType);
            setFlags((prev) => prev.filter((x) => x.flag_type !== flagType));
        } catch (e) { setError(verifError(e)); }
    };

    const runFaceCheck = async () => {
        setFaceChecking(true); setFaceMsg(null);
        try {
            const r = await APIAdminFaceCheck(token, item.id);
            if (!r.available) {
                setFaceMsg({ color: 'neutral', text: r.error || 'Провайдер лицо-матчинга не настроен' });
            } else {
                setFaceMsg({
                    color: r.match ? 'success' : 'warning',
                    text: `Совпадение лиц: ${Math.round((r.score || 0) * 100)}%${r.match ? ' — вероятно, один человек' : ''}`,
                });
            }
        } catch (e) { setFaceMsg({ color: 'danger', text: verifError(e) }); }
        finally { setFaceChecking(false); }
    };

    const runDupes = async () => {
        setDupesLoading(true);
        try { const r = await APIAdminProfilePhotoDuplicates(token, item.profile_id); setDupes(r?.items || []); }
        catch (e) { setError(verifError(e)); }
        finally { setDupesLoading(false); }
    };

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 620, width: '100%', maxHeight: '92vh', overflow: 'auto' }}>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
                ) : !v ? (
                    <Alert color="danger">{error || 'Не найдено'}</Alert>
                ) : (
                    <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography level="h4">Заявка #{v.id}</Typography>
                            <Chip color={(STATUS[v.status] || {}).color} variant="soft">
                                {(STATUS[v.status] || {}).label}
                            </Chip>
                        </Stack>
                        <Box sx={{ mt: 1 }}>
                            <AiStatusBadge status={v.ai_status} reason={v.ai_reason} confidence={v.ai_confidence} />
                        </Box>
                        {error && <Alert color="danger" sx={{ my: 1 }}>{error}</Alert>}

                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ my: 2 }} flexWrap="wrap">
                            <Photo label="Фото анкеты" url={v.profile_photo_url} />
                            <Photo label="Селфи" url={v.selfie_url} />
                            <Photo label="Документ" url={v.document_url} />
                        </Stack>

                        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1 }}>
                            <Box><Typography level="body-xs" color="neutral">ФИО</Typography>
                                <Typography level="body-sm">{v.full_name || '—'}</Typography></Box>
                            <Box><Typography level="body-xs" color="neutral">Дата рождения</Typography>
                                <Typography level="body-sm">{v.birth_date || '—'}</Typography></Box>
                            {v.face_match_score != null && (
                                <Box><Typography level="body-xs" color="neutral">Совпадение лиц</Typography>
                                    <Typography level="body-sm">{Math.round(v.face_match_score * 100)}%</Typography></Box>
                            )}
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />
                        <Typography level="title-sm" sx={{ mb: 1 }}>Риск-метки</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                            {flags.length === 0 && <Typography level="body-sm" color="neutral">Меток нет</Typography>}
                            {flags.map((f) => (
                                <Chip key={f.flag_type} color="danger" variant="soft"
                                      endDecorator={<span style={{ cursor: 'pointer' }} onClick={() => removeFlag(f.flag_type)}>×</span>}>
                                    {f.flag_label}
                                </Chip>
                            ))}
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                            <Select size="sm" placeholder="Добавить метку" value={newFlag} sx={{ minWidth: 180 }}
                                    onChange={(_, val) => setNewFlag(val ?? '')}>
                                {available.map((a) => <Option key={a.value} value={a.value}>{a.label}</Option>)}
                            </Select>
                            <Input size="sm" placeholder="Комментарий" value={flagNote}
                                   onChange={(e) => setFlagNote(e.target.value)} sx={{ flex: 1 }} />
                            <Button size="sm" variant="soft" disabled={!newFlag} onClick={addFlag}>Добавить</Button>
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />
                        <Typography level="title-sm" sx={{ mb: 1 }}>Антифрод</Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                            <Button size="sm" variant="outlined" loading={faceChecking} onClick={runFaceCheck}>Проверить лица</Button>
                            <Button size="sm" variant="outlined" loading={dupesLoading} onClick={runDupes}>Найти дубли фото</Button>
                        </Stack>
                        {faceMsg && <Alert size="sm" color={faceMsg.color} sx={{ mb: 1 }}>{faceMsg.text}</Alert>}
                        {dupes !== null && (
                            <Box sx={{ mb: 1 }}>
                                {dupes.length === 0 ? (
                                    <Typography level="body-sm" color="neutral">Дублей фото не найдено.</Typography>
                                ) : (
                                    <Stack spacing={0.5}>
                                        <Typography level="body-xs" color="danger">Похожие фото ({dupes.length}):</Typography>
                                        {dupes.map((d) => (
                                            <Stack key={d.profile_id} direction="row" spacing={1} alignItems="center">
                                                {d.photo_url && <img src={d.photo_url} alt="" width={32} height={32} style={{ objectFit: 'cover', borderRadius: 4 }} />}
                                                <Typography level="body-xs">
                                                    {d.name} · {d.city} · расстояние {d.distance}{d.is_verified ? ' · верифиц.' : ''}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        )}

                        <Divider sx={{ my: 1.5 }} />
                        <FormControl sx={{ mb: 1 }}>
                            <FormLabel>Заметка / что исправить</FormLabel>
                            <Textarea minRows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                        </FormControl>
                        <FormControl sx={{ mb: 1.5 }}>
                            <FormLabel>Причина отклонения</FormLabel>
                            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
                        </FormControl>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Button color="success" loading={busy} onClick={approve}>Подтвердить</Button>
                            <Button color="danger" variant="outlined" loading={busy} onClick={reject}>Отклонить</Button>
                            <Button color="warning" variant="outlined" loading={busy} onClick={requestChanges}>На доработку</Button>
                            <Button variant="plain" color="neutral" onClick={onClose}>Закрыть</Button>
                        </Stack>
                    </>
                )}
            </ModalDialog>
        </Modal>
    );
}

export default function AdminVerificationsPage() {
    const { adminToken: token } = useAdmin();
    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({});
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('pending');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => { setPage(1); setSearch(searchInput); }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true); setError(null);
        try {
            const r = await APIAdminVerifications(token, { status: status || undefined, search: search || undefined, page, page_size: 30 });
            setItems(r?.items || []); setCounters(r?.counters || {});
            setPages(r?.pages || 1); setTotal(r?.total || 0);
        } catch (e) { setError(verifError(e, 'Не удалось загрузить заявки')); } finally { setLoading(false); }
    }, [token, status, search, page]);
    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ mb: 0.5, gap: 1 }}>
                <Typography level="h4">Верификация анкет</Typography>
                <AiModerationModeToggle
                    token={token}
                    settingKey="ai_profile_moderation_mode"
                    description="Режим ИИ-модерации анкет: off/advisor/enforcer"
                    title="ИИ-модерация анкет"
                />
            </Stack>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>Всего: {total}</Typography>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <Input size="sm" placeholder="ФИО или имя анкеты" value={searchInput}
                           onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
                    <Select size="sm" value={status} sx={{ minWidth: 190 }}
                            onChange={(_, v) => { setStatus(v ?? ''); setPage(1); }}>
                        {STATUS_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                    </Select>
                    <Stack direction="row" spacing={1}>
                        <Chip size="sm" color="warning" variant="soft">на проверке: {counters.pending ?? 0}</Chip>
                        <Chip size="sm" color="success" variant="soft">подтв.: {counters.approved ?? 0}</Chip>
                        <Chip size="sm" color="danger" variant="soft">откл.: {counters.rejected ?? 0}</Chip>
                    </Stack>
                </Stack>
            </Sheet>

            {loading ? <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack> : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead><tr><th style={{ width: 56 }}></th><th>Анкета</th><th>ФИО</th><th style={{ width: 120 }}>Дата рожд.</th><th style={{ width: 130 }}>Город</th><th style={{ width: 130 }}>Статус</th></tr></thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} style={{ cursor: 'pointer' }} onClick={() => setDetail(it)}>
                                    <td>{it.profile_photo_url && <img src={it.profile_photo_url} alt="" width={40} height={40} style={{ objectFit: 'cover', borderRadius: 6 }} />}</td>
                                    <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{it.profile_name}</Typography></td>
                                    <td>{it.full_name || '—'}</td>
                                    <td>{it.birth_date || '—'}</td>
                                    <td>{it.city || '—'}</td>
                                    <td><Chip size="sm" color={(STATUS[it.status] || {}).color} variant="soft">{(STATUS[it.status] || {}).label}</Chip></td>
                                </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan={6}><Typography level="body-sm" color="neutral" sx={{ py: 3, textAlign: 'center' }}>Заявок нет</Typography></td></tr>}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                    <Button size="sm" variant="outlined" color="neutral" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Назад</Button>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>{page} / {pages}</Typography>
                    <Button size="sm" variant="outlined" color="neutral" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Вперёд</Button>
                </Stack>
            )}

            {detail && (
                <DetailModal token={token} item={detail} onClose={() => setDetail(null)} onChanged={load} />
            )}
        </Box>
    );
}